<template>
  <div class="page-showhomes-search">
    <h1 class="sr-only">
      Search Showhomes
    </h1>
    <ListingSearchResults />
  </div>
</template>

<script lang="ts" setup>
import {useListingStore} from '~/store/listing';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const listingStore = useListingStore();
const {$listing} = useNuxtApp();
const hasMounted = ref(false);
const route = useRoute();

/**
 * @_SERVER_PREFETCH Hook
 */
onServerPrefetch(async () => {
  await setListingDataResponse();
});

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const title = 'Showhomes | New Homes - Explore New Showhomes in New Zealand';
const description = 'Discover New Zealand\'s top showhomes on New Homes. Get details like name, address, contact details, images, and map.';
useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
});

/**
 * @_MOUNTED Hook
 */
onMounted(async () => {
  nextTick(() => {
    hasMounted.value = true;
  });

  listingStore.setIsLoading(true);
  setListingDataResponse();
});

/**
 * @_WATCH route.query
 */
watch(() => route.query, async () => {
  const listingQuery = ListingSearchQueryHandler.get();
  if (listingQuery.listingTypes.includes('Showhome') && listingQuery.listingTypes.length === 1) {
    await setListingDataResponse();
  } else {
    ListingSearchQueryHandler.set(listingQuery, '/listing/search');
  }
});

/**
 * Set the listing data
 */
async function setListingDataResponse () {
  listingStore.setIsLoading(true);
  listingStore.setCompanySlug(null);
  setShowhomeQueryParams();
  const listingResultResponse = await ($listing as any).search();
  listingStore.setListingData(listingResultResponse);
  listingStore.setIsLoading(false);
}

/**
 * Set the showhome query params
 */
function setShowhomeQueryParams () {
  const routeQuery = ListingSearchQueryHandler.get();
  if (!routeQuery.listingTypes || routeQuery.listingTypes.length === 0 || routeQuery.listingTypes[0] !== 'Showhome') {
    ListingSearchQueryHandler.set({
      listingTypes: ['Showhome'],
    }, '/showhomes/search');
  }
}
</script>
